import { Injectable } from '@angular/core';
import { UserDto } from '../../../auth/user.dto';
import { ContractDto } from '../../../beneficiaries/beneficiary-folder/contract/contract.dto';
import { Clearances } from '../../../shared/models/enums/clearance.enum';
import { ContractTraceabilityStatus } from '../../traceability/contract-traceability-status.enum';
import { TPermission } from '../permissions';
import { IPermissionService } from './permission.service.interface';

type PermissionsByStatus = { [key in ContractTraceabilityStatus]: TPermission[] };
type ContractPermission = {
  ['ADMIN']: { state: PermissionsByStatus };
  ['STRUCTURE']: { state: PermissionsByStatus };
  ['DIRECTION+']: { state: PermissionsByStatus };
};

@Injectable({
  providedIn: 'root',
})
export class ContractPermissionService implements IPermissionService<ContractDto> {
  protected workflowAccess(user: UserDto): ContractPermission {
    return {
      ['ADMIN']: {
        state: {
          [ContractTraceabilityStatus.Creation]: ['CAN_EDIT', 'CAN_DELETE', 'CONTRACT_IER_CAN_BENEF_SIGN', 'CONTRACT_SOCIO_PRO_CAN_BENEF_SIGN'],
          [ContractTraceabilityStatus.Unvalidated]: ['CAN_EDIT', 'CAN_DELETE', 'CONTRACT_IER_CAN_BENEF_SIGN', 'CONTRACT_SOCIO_PRO_CAN_BENEF_SIGN'],
          [ContractTraceabilityStatus.SignedBeneficiary]: [
            'CAN_DELETE',
            'CONTRACT_IER_CAN_ROLLBACK',
            'CONTRACT_IER_CAN_VALIDATE',
            'CONTRACT_IER_CAN_REJECT',
            'CONTRACT_SOCIO_PRO_CAN_ROLLBACK',
            'CONTRACT_SOCIO_PRO_CAN_VALIDATE',
            'CONTRACT_SOCIO_PRO_CAN_REJECT',
          ],
          [ContractTraceabilityStatus.ValidatedMetro]: [
            'CAN_DELETE',
            'CONTRACT_IER_CAN_SIGN',
            'CONTRACT_IER_CAN_REJECT',
            'CONTRACT_SOCIO_PRO_CAN_SIGN',
            'CONTRACT_SOCIO_PRO_CAN_REJECT',
          ],
          [ContractTraceabilityStatus.SignedMetro]: ['CAN_DELETE', 'CONTRACT_IER_CAN_CLOSE', 'CONTRACT_SOCIO_PRO_CAN_CLOSE'],
          [ContractTraceabilityStatus.Done]: ['CAN_DELETE', 'CONTRACT_IER_CAN_REOPEN', 'CONTRACT_SOCIO_PRO_CAN_REOPEN'],
        },
      },
      ['STRUCTURE']: {
        state: {
          [ContractTraceabilityStatus.Creation]: ['CAN_EDIT', 'CAN_DELETE', 'CONTRACT_IER_CAN_BENEF_SIGN', 'CONTRACT_SOCIO_PRO_CAN_BENEF_SIGN'],
          [ContractTraceabilityStatus.Unvalidated]: ['CAN_EDIT', 'CAN_DELETE', 'CONTRACT_IER_CAN_BENEF_SIGN', 'CONTRACT_SOCIO_PRO_CAN_BENEF_SIGN'],
          [ContractTraceabilityStatus.SignedBeneficiary]: ['CAN_DELETE', 'CONTRACT_IER_CAN_ROLLBACK', 'CONTRACT_SOCIO_PRO_CAN_ROLLBACK'],
          [ContractTraceabilityStatus.ValidatedMetro]: ['CAN_DELETE'],
          [ContractTraceabilityStatus.SignedMetro]: ['CONTRACT_IER_CAN_CLOSE', 'CONTRACT_SOCIO_PRO_CAN_CLOSE'],
          [ContractTraceabilityStatus.Done]: ['CONTRACT_IER_CAN_REOPEN', 'CONTRACT_SOCIO_PRO_CAN_REOPEN'],
        },
      },
      ['DIRECTION+']: {
        state: {
          [ContractTraceabilityStatus.SignedBeneficiary]: (['CAN_DELETE'] as TPermission[])
            .concat(
              (user.clearances.some((value) => value === Clearances.CLR_VALIDATOR_IER)
                ? ['CONTRACT_IER_CAN_VALIDATE', 'CONTRACT_IER_CAN_REJECT']
                : []) as TPermission[],
            )
            .concat(
              (user.clearances.some((value) => value === Clearances.CLR_VALIDATOR_SOCIO_PRO)
                ? ['CONTRACT_SOCIO_PRO_CAN_VALIDATE', 'CONTRACT_SOCIO_PRO_CAN_REJECT']
                : []) as TPermission[],
            ),
          [ContractTraceabilityStatus.ValidatedMetro]: (['CAN_DELETE'] as TPermission[])
            .concat(
              user.clearances.some((value) => value === Clearances.CLR_SIGNATORY_IER) ? ['CONTRACT_IER_CAN_SIGN', 'CONTRACT_IER_CAN_REJECT'] : [],
            )
            .concat(
              user.clearances.some((value) => value === Clearances.CLR_SIGNATORY_SOCIO_PRO)
                ? ['CONTRACT_SOCIO_PRO_CAN_SIGN', 'CONTRACT_SOCIO_PRO_CAN_REJECT']
                : [],
            ),
          [ContractTraceabilityStatus.Creation]: undefined,
          [ContractTraceabilityStatus.Unvalidated]: undefined,
          [ContractTraceabilityStatus.SignedMetro]: undefined,
          [ContractTraceabilityStatus.Done]: undefined,
        },
      },
    };
  }

  public getPermissions(user: UserDto, subject: ContractDto): TPermission[] {
    return this.workflowAccess(user)[user.groups[0]]?.state[subject.status];
  }
}
